import React, { FC, useContext, useState } from 'react';
import { Box, Switch, Typography } from '@material-ui/core';
import AppContext from '../../context/AppContext';
import { useShopConfigV2 } from '../../hooks/shopConfig';
import BackgroundRemovalModalBase from './BackgroundRemovalModalBase';
import {
  BackgroundRemovalErrorResponseCode,
  handleRemoveBackground,
} from './backgroundRemovalUtils';
import { handle } from '../../util/helpers';
import { useOwnListingVersions } from '../../containers/EditListingPage/hooks/useOwnListingVersions';

interface DefaultBackgroundRemovalModalProps {
  originalImageSrc: string;
  savedBackgroundRemovedImageSrc?: string | null;
  open: boolean;
  onClose: () => void;
  onModalSubmit: (toggled: boolean, base64ImageToUpload?: string) => void;
  initialToggleChecked?: boolean;
  isUploading: boolean;
  hasUploadError: boolean;
}

const DefaultBackgroundRemovalModal: FC<DefaultBackgroundRemovalModalProps> = (
  props: DefaultBackgroundRemovalModalProps
) => {
  const {
    originalImageSrc,
    savedBackgroundRemovedImageSrc,
    open,
    onClose,
    onModalSubmit,
    isUploading,
    initialToggleChecked,
    hasUploadError,
  } = props;
  const { treetId } = useContext(AppContext);
  const [toggleChecked, setToggleChecked] = useState(
    !!(initialToggleChecked && savedBackgroundRemovedImageSrc)
  );
  const { shopName } = useShopConfigV2();
  const { currentListing } = useOwnListingVersions();
  const [tempBGRemovedImageBase64, setTempBGRemovedImageBase64] = useState('');
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [removeBackgroundError, setRemoveBackgroundError] = useState<
    string | BackgroundRemovalErrorResponseCode | null
  >(null);
  const hasBackgroundRemovedImage = savedBackgroundRemovedImageSrc || tempBGRemovedImageBase64;
  const backgroundRemovedImageSrc =
    savedBackgroundRemovedImageSrc || `data:image/jpeg;base64,${tempBGRemovedImageBase64}`;

  return (
    <BackgroundRemovalModalBase
      open={open}
      onClose={onClose}
      onModalSubmit={() => onModalSubmit(toggleChecked, tempBGRemovedImageBase64)}
      imageSrc={toggleChecked ? backgroundRemovedImageSrc : originalImageSrc}
      isUploading={isUploading}
      hasUploadError={hasUploadError}
      isRemovingBackground={isRemovingBackground}
      removeBackgroundError={removeBackgroundError}
      helperText="Photos with their background removed make an item sell faster!"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" pt={2}>
        <Typography variant="body1">Remove Background</Typography>
        <Switch
          checked={toggleChecked}
          color="default"
          onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
            const isChecked = event.target.checked;
            setToggleChecked(isChecked);
            if (isChecked && !hasBackgroundRemovedImage) {
              setIsRemovingBackground(true);
              const [tempBase64, error] = await handle(
                handleRemoveBackground({
                  imageUrl: originalImageSrc,
                  currentListingUuid: currentListing?.id?.uuid,
                  treetId,
                  shopName,
                })
              );
              if (error) {
                setRemoveBackgroundError(error?.code || 'error');
              } else {
                setTempBGRemovedImageBase64(tempBase64);
              }
              setIsRemovingBackground(false);
            } else if (!isChecked) {
              setRemoveBackgroundError(null);
            }
          }}
        />
      </Box>
    </BackgroundRemovalModalBase>
  );
};
export default DefaultBackgroundRemovalModal;
